import { Dialog } from "@shared/EDS/components/Dialog";
import { JSX, useId, useState } from "react";
import { GenerateSolutionContent } from "./GenerateSolutionContent";
import { useDialogStore } from "@shared/common/contexts/dialog/useDialogStore";
import { useKnopClient } from "@opstel/contexts/knopClient/useKnopClient";
import { GenerateSolutionRequest } from "@opstel/gen/knop/ui/v1/api/hip_pb";
import { useCurrentInstanceMetadata } from "@shared/common/contexts/instanceMetadata/useCurrentInstanceMetadata";
import { EmptyPlanContent } from "./EmptyPlanContent";
import { useMutation } from "@tanstack/react-query";
import { messageToast } from "@shared/EDS/components/MessageToast/messageToastFunctions";
import { GenerateSolutionFailedDialog } from "./GenerateSolutionFailedDialog";

export function GenerateSolutionDialog(): JSX.Element {
  const generateSolutionDialogId = useId();
  const generateSolutionFailedDialogId = useId();
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const { data: instanceMetadata } = useCurrentInstanceMetadata();
  const { knopClient } = useKnopClient();
  const [toHipConversionErrors, setToHipConversionErrors] = useState<string[]>([]);

  function closeGenerateSolutionDialog() {
    closeDialog(generateSolutionDialogId);
  }

  function openGenerateSolutionFailedDialog() {
    openDialog(generateSolutionFailedDialogId);
  }

  const generateSolution = useMutation({
    mutationFn: (generateSolutionRequest: GenerateSolutionRequest) => {
      closeGenerateSolutionDialog();
      return knopClient.generateSolution(generateSolutionRequest);
    },
    onError: (err) => {
      console.error("Error generating solution:", err);
      messageToast({
        message: `Het is niet gelukt een oplossing aan te vragen`,
        type: "error",
      });
    },
    onSuccess: (data) => {
      if (data.status === "SUCCESS") {
        messageToast({ message: `Oplossing genereren is gestart` });
        return;
      }
      setToHipConversionErrors(data.toHipConversionErrors);
      if (data.toHipConversionErrors.length === 0) {
        messageToast({
          message: `Het is niet gelukt een oplossing aan te vragen`,
        });
        closeGenerateSolutionDialog();
        return;
      }
      closeGenerateSolutionDialog();
      openGenerateSolutionFailedDialog();
    },
  });

  return (
    <>
      <tractie-button
        data-testid="generate-solution-open-button"
        role="button"
        level="primary"
        size="m"
        onClick={() => openDialog(generateSolutionDialogId)}
      >
        Knoopplan genereren
      </tractie-button>
      <Dialog
        dialogId={generateSolutionDialogId}
        title="Knoopplan genereren"
        maxWidth="496px"
        description={
          instanceMetadata
            ? "Er zal automatisch een knoopplan worden gemaakt door middel van solver HIP."
            : undefined
        }
      >
        {instanceMetadata ? (
          <GenerateSolutionContent
            onCancel={closeGenerateSolutionDialog}
            onGenerateSolution={generateSolution.mutate}
            instanceMetadata={instanceMetadata}
          />
        ) : (
          <EmptyPlanContent onClose={closeGenerateSolutionDialog} />
        )}
      </Dialog>
      <GenerateSolutionFailedDialog
        dialogId={generateSolutionFailedDialogId}
        toHipConversionErrors={toHipConversionErrors}
      />
    </>
  );
}
